import React, { useState, useRef, useEffect } from 'react';
import { Lightbulb, Edit, FileText, MessageSquare, Send, CheckCircle, RefreshCw, ArrowRight, ChevronDown, ChevronUp, Award, Clock } from 'lucide-react';
import { AI_CONFIG } from '../services/innovatechMockAPI';

// 🎯 Componente específico para InnovaTech - LIMPIO y ENFOCADO
const InnovaTechCard = ({ 
    title, 
    description, 
    onSubmit, 
    isProcessing = false,
    placeholder = "Escribe tu hipótesis aquí...",
    showTemplate = true,
    onInputChange = null,  // 📊 Nuevo: callback para persistencia de input libre
    initialValue = "",     // 📊 Nuevo: valor inicial desde DB
    approvalData = null    // 🎯 Nuevo: datos de aprobación IA {approved, score, aiResponse}
}) => {
    const [inputValue, setInputValue] = useState(initialValue); // 📊 Inicializar con valor de DB
    const [templateValid, setTemplateValid] = useState(null);
    const [hasAnalyzed, setHasAnalyzed] = useState(false);
    const [lastSubmittedValue, setLastSubmittedValue] = useState(''); // 📝 Mantener último valor enviado
    const [isEditMode, setIsEditMode] = useState(false); // 🔄 Modo de edición activo
    const [showFeedbackPanel, setShowFeedbackPanel] = useState(false); // 📋 Panel de feedback expandible
    
    // 🔄 Sincronizar con valor inicial cuando cambie (DB como fuente de verdad)
    useEffect(() => {
        if (initialValue && initialValue !== inputValue) {
            setInputValue(initialValue);
            console.log('[📊 InnovaTechCard] Sincronizando con DB:', initialValue);
        }
    }, [initialValue]);

    // ⏳ Debounce para persistencia de input (🔇 DESHABILITADO: Evita spam al backend)
    // useEffect(() => {
    //     if (!inputValue || !onInputChange) return;
    //     
    //     const debounceTimer = setTimeout(() => {
    //         const inputData = {
    //             content: inputValue,
    //             tokens_count: Math.ceil(inputValue.length / 4),
    //             language: 'es',
    //             word_count: inputValue.trim().split(/\s+/).filter(word => word.length > 0).length,
    //             client_ts: new Date().toISOString(),
    //             event_type: 'input_libre'
    //         };
    //         onInputChange(inputData);
    //     }, 1000); // 1 segundo de debounce
    // 
    //     return () => clearTimeout(debounceTimer);
    // }, [inputValue, onInputChange]);
    
    // 📍 Referencia al textarea para auto-focus
    const textareaRef = useRef(null);

    // 🎯 Validación según gating pedagógico InnovaTech Lab1
    const validateTemplate = (text) => {
        if (!text) {
            return { valid: false, message: "Escribe tu hipótesis", isComplete: false };
        }
        
        // Criterio 1: Longitud mínima según configuración
        const minLength = AI_CONFIG.hypothesis_template.min_length;
        if (text.length < minLength) {
            return { 
                valid: false, 
                message: `Mínimo ${minLength} caracteres para completar Lab1 (actual: ${text.length})`,
                isComplete: false
            };
        }
        
        // Criterio 2: Formato estructurado según JSON (fuente de verdad)
        const lowerText = text.toLowerCase();
        const hasClientePattern = lowerText.includes('creemos que');
        const hasProblemPattern = lowerText.includes('tienen');
        const hasReasonPattern = lowerText.includes('porque');
        
        if (!hasClientePattern || !hasProblemPattern || !hasReasonPattern) {
            return {
                valid: false,
                message: "Usa el formato: Creemos que [grupo de clientes] tienen [problema] porque [razón observable]",
                isComplete: false
            };
        }
        
        // ✅ Validación completada
        return {
            valid: true,
            message: "✅ Hipótesis válida - Lista para analizar",
            isComplete: true
        };
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        
        // 🔄 Salir del modo edición cuando el usuario empiece a escribir
        if (isEditMode && value !== lastSubmittedValue) {
            setIsEditMode(false);
        }
        
        // 🔇 Persistencia automática DESHABILITADA para evitar spam al backend
        
        if (value.length > 10) {
            const validation = validateTemplate(value);
            setTemplateValid(validation);
        } else {
            setTemplateValid(null);
        }
    };

    const handleSubmit = () => {
        if (inputValue.trim() && templateValid?.valid) {
            // 💾 Guardar el valor enviado para poder editarlo después
            setLastSubmittedValue(inputValue);
            onSubmit(inputValue);
            setHasAnalyzed(true); // Marca que ya se analizó la hipótesis
            // ✅ NO limpiar el input - mantener el texto para que no se pierda
            // setInputValue(''); // ← REMOVIDO
            setTemplateValid(null);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            {/* 📋 Header con badge de aprobación */}
            <div className="text-center mb-6 p-6 pb-0 relative">
                {/* 🎯 Badge de aprobación - Esquina superior derecha */}
                {approvalData?.approved && (
                    <div className="absolute top-4 right-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 shadow-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-700">
                            {Math.round(approvalData.score)}/100
                        </span>
                    </div>
                )}
                
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className={`w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center ${
                        approvalData?.approved 
                            ? 'from-emerald-500 to-green-600' 
                            : 'from-green-500 to-emerald-600'
                    }`}>
                        <Lightbulb className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">
                        {title}
                    </h2>
                </div>
                <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                    {description}
                </p>
            </div>

            {/* 🎯 FORMULARIO PRINCIPAL - Solo visible cuando NO está analizado O está en modo edición */}
            {(!hasAnalyzed || isEditMode) && (
                <div className="px-6">
                    {/* 📝 Template Guide */}
                    {showTemplate && (
                        <div className="mb-6 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2 text-indigo-700 font-semibold">
                                    <FileText className="w-5 h-5" />
                                    Template Obligatorio
                                </div>
                                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">
                                    📏 Mín. {AI_CONFIG.hypothesis_template.min_length} caracteres
                                </span>
                            </div>
                            
                            <div className="text-sm text-indigo-800 font-mono bg-white/70 p-4 rounded-lg mb-4 border border-indigo-100">
                                <span className="font-bold text-indigo-900">Creemos que</span> <span className="text-purple-700 font-semibold">[grupo de clientes]</span>
                                <span className="font-bold text-indigo-900"> tienen</span> <span className="text-purple-700 font-semibold">[problema]</span>
                                <span className="font-bold text-indigo-900"> porque</span> <span className="text-purple-700 font-semibold">[razón observable]</span>
                            </div>
                            
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-green-600 text-sm font-medium">✅ Ejemplo:</span>
                                    <div className="text-sm text-green-800 leading-relaxed">
                                        "Creemos que <strong>estudiantes universitarios de ingeniería</strong> tienen <strong>dificultades para encontrar prácticas relevantes</strong> porque <strong>las plataformas actuales no filtran por especialización técnica</strong>."
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ✏️ Input Area */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-700 font-medium">
                            <MessageSquare className="w-4 h-4" />
                            Describe tu hipótesis usando el template estructurado
                        </div>

                        <div className="space-y-2">
                            <textarea
                                ref={textareaRef}
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder={placeholder}
                                className={`w-full min-h-[120px] p-4 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none transition-all ${
                                    isEditMode 
                                        ? 'border-blue-300 bg-blue-50 shadow-md' 
                                        : 'border-gray-300'
                                }`}
                                disabled={isProcessing}
                            />
                            
                            {/* 🎯 Validación en tiempo real - Movido debajo */}
                            {templateValid && (
                                <div className={`text-xs px-2 py-1 rounded ${
                                    templateValid.valid 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {templateValid.message}
                                </div>
                            )}
                        </div>

                        {/* 🔄 Indicador de modo edición */}
                        {isEditMode && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
                                <div className="flex items-center gap-2">
                                    <Edit className="w-4 h-4 text-blue-600" />
                                    <span className="text-blue-800 text-sm font-medium">
                                        Modo edición activado - Mejora tu hipótesis
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* 🚀 Submit Button - Solo se muestra si no se ha analizado */}
                        {!hasAnalyzed && (
                            <div className="flex justify-end">
                                <button
                                onClick={handleSubmit}
                                disabled={isProcessing || !templateValid?.valid}
                                className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                                    isProcessing || !templateValid?.valid
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                                }`}
                            >
                                {isProcessing ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Analizando...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Analizar Hipótesis
                                    </>
                                )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 🔄 Botones de iteración pedagógica - FUERA del formulario, aparecen después del análisis */}
            {hasAnalyzed && (
                <div className="mx-6 mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-800 font-medium">Hipótesis analizada por la IA</span>
                    </div>
                    
                    {/* 📝 Mostrar la hipótesis enviada como referencia */}
                    {lastSubmittedValue && (
                        <div className="mb-4 p-3 bg-white border-l-4 border-green-400 rounded-r-lg">
                            <p className="text-sm text-gray-600 font-medium mb-1">Tu hipótesis:</p>
                            <p className="text-gray-800 text-sm italic">"{lastSubmittedValue}"</p>
                        </div>
                    )}
                    
                    <p className="text-green-700 text-sm mb-4">
                        ¿Quieres iterar y mejorar tu hipótesis antes de continuar?
                    </p>
                    
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                setHasAnalyzed(false);
                                setIsEditMode(true);
                                // 🔄 Restaurar el último valor enviado para edición
                                setInputValue(lastSubmittedValue);
                                setTemplateValid(null);
                                
                                // 📍 Auto-focus en el textarea con delay para renderizado
                                setTimeout(() => {
                                    if (textareaRef.current) {
                                        textareaRef.current.focus();
                                        // 📝 Posicionar cursor al final del texto
                                        textareaRef.current.setSelectionRange(
                                            lastSubmittedValue.length, 
                                            lastSubmittedValue.length
                                        );
                                        
                                        // 🎯 Scroll suave hacia el textarea
                                        textareaRef.current.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'center'
                                        });
                                    }
                                }, 100);
                            }}
                            className="px-4 py-2 bg-white border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2 shadow-sm hover:shadow-md"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Mejorar Hipótesis
                        </button>
                        

                    </div>
                </div>
            )}

            {/* 📋 Panel de Feedback IA - Expandible */}
            {approvalData?.aiResponse && (
                <div className="mx-6 mb-6">
                    <button
                        onClick={() => setShowFeedbackPanel(!showFeedbackPanel)}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <Award className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-gray-800">Ver análisis IA completo</span>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4" />
                                {new Date(approvalData.evaluatedAt).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </div>
                        </div>
                        {showFeedbackPanel ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </button>
                    
                    {showFeedbackPanel && (
                        <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                            <div className="space-y-4">
                                {/* Score detallado */}
                                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                                    <span className="font-medium text-emerald-800">Puntuación Final</span>
                                    <span className="text-2xl font-bold text-emerald-700">
                                        {Math.round(approvalData.score)}/100
                                    </span>
                                </div>
                                
                                {/* Contenido del feedback */}
                                <div className="prose prose-sm max-w-none">
                                    <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                        {approvalData.aiResponse.content}
                                    </div>
                                </div>
                                
                                {/* Metadata */}
                                <div className="pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                                    <span>Rol: {approvalData.aiResponse.role}</span>
                                    <span>Estado: {approvalData.approved ? 'Aprobada' : 'Requiere mejoras'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            <div className="pb-6"></div>
        </div>
    );
};

export default InnovaTechCard;